import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useMediaQuery } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { focusRing, heading, meta, mono, pageSurface } from '../../../styles/visualLanguage';
import { AdvisoryLeaveButton } from './AdvisoryLeaveButton';
import { isRunInFlight, type AdvisoryRunView } from '../advisoryRunView';
import { ROOT_PHASE, runRows, type RunRowState } from '../advisoryRunRows';

/**
 * What an engagement run looks like while it happens.
 *
 * Replaces a bare "Gaia is working on your goal" that stood for twenty minutes
 * with no indication of progress. A wait that long has to say what it is waiting
 * for, or it is indistinguishable from a hang.
 *
 * Purely presentational, and deliberately source-agnostic: it renders an
 * `AdvisoryRunView`, which either the SSE stream (live, with per-turn rows) or the
 * project document (counters only, but survives a reload) can produce. That is
 * what keeps one progress view instead of two that drift.
 *
 * Every row names the QUESTION it is answering, not the label it runs under. The
 * first version of this panel showed the labels — `root`, `earnings-quality`,
 * `earnings-quality/sq2` — beside a phase name and a count of "15 retrieved
 * references", and none of those three tells a reader what the step was, what a
 * "follow-up" follows from, or what a reference is. Each row now leads with its
 * question and opens into a detail panel saying what the step is for and what its
 * references are.
 *
 * It lists one row per KEY QUESTION, not one per turn: `advisoryRunRows.ts` folds
 * each chain into the question it hangs off, carrying the branch's state, counts
 * and time up with it. See that module for why folding a chain away is a state
 * problem before it is a layout one.
 *
 * It renders NO error text. A failed turn is a status, never a message — the wire
 * carries raw pgms prose and this client never puts that on screen (ASVS V7). The
 * same rule is why no detail panel shows an answer: what a step ANSWERED is model
 * prose, and this panel reports what happened, not what was said.
 *
 * It carries the same leave affordance as every counted wizard step
 * (`AdvisoryLeaveButton`). A run is a place the user stands for twenty minutes, and
 * this was the one advisory surface whose only exit was the browser's own Back
 * button. Leaving costs nothing: `useSupervisorTurn` deliberately does not abort on
 * unmount, and the four analysis stages are derived from `analysis.status`, so
 * re-opening the engagement from the list lands back on this panel. That is what the
 * leave-and-return note has always promised; nothing on screen acted on it.
 *
 * The affordance sits OUTSIDE the live region on purpose. One polite `role="status"`
 * wraps the changing panel body so a finished step is announced; chrome inside it
 * would be re-announced along with every turn that lands.
 */
export interface AdvisoryRunProgressProps {
  view: AdvisoryRunView;
  embedded?: boolean;
  /**
   * Narration from a `status` SSE event, when one has arrived. Passed through
   * rather than invented: nothing here fabricates a step or a timer.
   */
  progressPhase?: string | null;
}

/**
 * The six DISPLAY states, mapped from seven possible wire values per turn, plus
 * `skipped` derived for planned work an upstream failure prevented from running,
 * and then folded across a branch by `runRowState`.
 *
 * agent-svc can report `planned`, `pending`, `running`, `completed`, `failed`,
 * `rejected` or `timeout`. A key per value would drift the moment it adds one, and
 * the distinctions a reader needs are fewer than the distinctions the wire makes:
 * a `rejected` turn and a `timeout` turn are both "this step did not happen".
 */
const STATE_KEYS: Record<RunRowState, string> = {
  pending: 'advisory:evidence.turnPending',
  running: 'advisory:evidence.turnRunning',
  done: 'advisory:evidence.turnDone',
  partial: 'advisory:evidence.turnPartial',
  failed: 'advisory:evidence.turnFailed',
  skipped: 'advisory:evidence.turnSkipped',
};

/**
 * Whether a row has measured anything worth reporting.
 *
 * `partial` counts: a branch that answered its question and lost one follow-up
 * did retrieve, and did take time. `failed` does not — there is nothing to claim.
 */
const hasMeasurements = (state: RunRowState): boolean => state === 'done' || state === 'partial';

/** `1-root` / `2-key-questions` / `3-subquestions` — the strings agent-svc emits. */
const PHASE_KEYS: Record<string, string> = {
  [ROOT_PHASE]: 'advisory:evidence.phaseRoot',
  '2-key-questions': 'advisory:evidence.phaseKeyQuestions',
  '3-subquestions': 'advisory:evidence.phaseSubquestions',
};

/**
 * The chevron's box, in px: a hit area its 20px glyph alone would not give it.
 *
 * Sized rather than padded. Padding is what made it disagree with the state mark
 * back when the two shared a vertical axis, and a size cannot drift the same way.
 */
const CHEVRON_BOX = 28;

/**
 * The state mark's slot inside the pill, in px.
 *
 * Fixed, because the two glyphs are not the same size (14px spinner, 16px tick)
 * and a pill that changed width when a step finished would twitch every time one
 * did — on a panel whose whole job is to make progress legible.
 */
const MARK_BOX = 16;

/**
 * Read by a screen reader, painted nowhere.
 *
 * `2/3` is precise on screen and bare read aloud. This says the same thing in the
 * run counter's own words, so the row and the headline are worded identically
 * rather than being two ways of saying one fact.
 */
const SR_ONLY = {
  position: 'absolute' as const,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap' as const,
  border: 0,
};

/**
 * The two states that read better as a mark than as a word.
 *
 * `running` and `done` are the states a reader scans for over a twenty-minute
 * wait, and a column of the words "running" and "done" is harder to scan than a
 * spinner and a tick. The other four keep their words: `queued`, `partly
 * answered`, `failed` and `not run` each say something a glyph would have to be
 * learned to mean.
 *
 * Both carry the SAME i18n string they replaced, as an `aria-label` — the status
 * has to survive for a screen reader, which is the whole point of the polite live
 * region this sits inside.
 */
function StateGlyph({ state, label }: { state: RunRowState; label: string }) {
  if (state === 'running') {
    return (
      <CircularProgress
        size={14}
        thickness={5}
        data-testid="advisory-run-turn-state-icon"
        aria-label={label}
        sx={theme => ({
          color: theme.colors.lavenderDeep,
          // The one animation in this panel, so it carries the guard the rest of
          // the visual language applies to its own transitions. Stopped, the arc
          // still reads as unfinished, and the label says so outright.
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        })}
      />
    );
  }
  return (
    <CheckIcon
      fontSize="small"
      role="img"
      data-testid="advisory-run-turn-state-icon"
      aria-label={label}
      sx={theme => ({ color: theme.colors.lavenderDeep, fontSize: '16px' })}
    />
  );
}

/**
 * One source, in the shape a search UI uses: a glyph, a title, and a line saying
 * what kind of thing it is.
 *
 * Both kinds a run can name go through this: a follow-up the question was broken
 * into, and an earlier step this one retrieved from. Neither carries a snippet,
 * and this deliberately has no place to put one — a reference is a passage from an
 * earlier step's ANSWER, and answers are model prose this panel does not render.
 */
function SourceCard({
  icon,
  marker,
  title,
  detail,
  testId,
}: {
  icon?: React.ReactNode;
  /** A step's own marker — the same `KQ 2` / `2.1` the list and the editor use. */
  marker?: string;
  title: string;
  detail: string;
  testId: string;
}) {
  return (
    <Box
      data-testid={testId}
      sx={theme => ({
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 1,
        alignItems: 'start',
        padding: 1,
        borderRadius: theme.visualPrimitives.radiusCard,
        border: `1px solid ${theme.visualPrimitives.borderLight}`,
      })}
    >
      <Box sx={theme => ({ color: theme.colors.inkTertiary, display: 'flex', paddingTop: '2px' })}>
        {marker ? (
          <Typography
            component="span"
            sx={[
              mono,
              theme => ({
                fontSize: theme.visualPrimitives.kqSubIndexSize,
                color: theme.colors.lavenderDeep,
                whiteSpace: 'nowrap',
              }),
            ]}
          >
            {marker}
          </Typography>
        ) : (
          icon
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={theme => ({
            fontSize: theme.visualPrimitives.dzSubSize,
            lineHeight: theme.visualPrimitives.kqSubTextLineHeight,
            color: theme.colors.inkPrimary,
          })}
        >
          {title}
        </Typography>
        {detail ? (
          <Typography
            sx={theme => ({
              fontSize: theme.visualPrimitives.kqSubIndexSize,
              color: theme.colors.inkTertiary,
            })}
          >
            {detail}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * How long a row took, in the unit that reads.
 *
 * Minutes above ninety seconds, because a step routinely takes several and
 * "230 s" is a number a reader has to divide. Units are abbreviated so one string
 * reads correctly for any count.
 */
function durationKeyAndValue(seconds: number): { key: string; values: Record<string, number> } {
  return seconds < 90
    ? {
        key: 'advisory:evidence.stepDurationSeconds',
        values: { seconds: Math.max(1, Math.round(seconds)) },
      }
    : {
        key: 'advisory:evidence.stepDurationMinutes',
        values: { minutes: Math.round(seconds / 60) },
      };
}

function titleKeyFor(view: AdvisoryRunView): string {
  if (isRunInFlight(view)) {
    return 'advisory:evidence.runTitle';
  }
  if (view.failed) {
    return 'advisory:evidence.runFailedTitle';
  }
  // `partial` is terminal and NOT a failure: a run where one branch died still
  // answered the others, so it gets its own heading rather than borrowing either.
  return view.status === 'partial'
    ? 'advisory:evidence.runPartialTitle'
    : 'advisory:evidence.runDoneTitle';
}

export function AdvisoryRunProgress({
  view,
  progressPhase,
  embedded = false,
}: AdvisoryRunProgressProps) {
  const { t } = useUntypedTranslation();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // Which steps the reader has opened. Keyed by label, which is unique per run,
  // and deliberately NOT reset when a turn transitions: a panel that snapped shut
  // every time another step finished would be unusable during a live run.
  const [expandedLabels, setExpandedLabels] = useState<Set<string>>(new Set());
  const inFlight = isRunInFlight(view);
  const total = view.plannedTurns;
  const done = view.completedTurns + view.failedTurns;
  // Guarded: `plannedTurns` is 0 on a document-sourced failed run, and 0/0 would
  // render NaN% on the bar.
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;

  const toggle = (label: string) =>
    setExpandedLabels(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });

  return (
    <Box
      sx={[
        pageSurface,
        { p: embedded ? 0 : 2.5, maxWidth: embedded ? 'none' : 640, marginX: 'auto' },
      ]}
    >
      {/* Chrome, not status: kept outside the live region below so it is not
          re-announced every time a turn lands. */}
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <AdvisoryLeaveButton />
        </Box>
      )}

      {/* One live region for the changing panel body, so a screen reader is told when
          a step finishes rather than the change passing silently over twenty minutes. */}
      <Box role="status" aria-live="polite">
        <Typography component={embedded ? 'h2' : 'h1'} sx={[heading]}>
          {t(titleKeyFor(view))}
        </Typography>

        {total > 0 ? (
          <>
            <Typography sx={[meta]} data-testid="advisory-run-progress-count">
              {t('advisory:evidence.runProgress', { done, total })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={percent}
              aria-hidden
              sx={{ marginY: 1.5, borderRadius: 1 }}
            />
          </>
        ) : null}

        {/* Only ever text from a `status` event that actually arrived. */}
        {progressPhase ? <Typography sx={[meta]}>{progressPhase}</Typography> : null}

        {/* The honest state before the first turn returns. On the local sync path the
            first row does not appear for minutes, and an empty panel would read as a
            hang. */}
        {inFlight && view.turns.length === 0 ? (
          <Typography sx={[meta]} data-testid="advisory-run-waiting">
            {t('advisory:evidence.runWaiting')}
          </Typography>
        ) : null}

        {view.turns.length > 0 ? (
          <Box
            component="ul"
            data-testid="advisory-run-turns"
            sx={{ listStyle: 'none', margin: 0, padding: 0 }}
          >
            {runRows(view.turns).map(row => {
              // The framing row carries its phase as its MARKER, so the phase line
              // below would print the same word twice on the same row.
              const phaseKey = row.kind === 'root' ? undefined : PHASE_KEYS[row.phase];
              const expanded = expandedLabels.has(row.label);
              const measured = hasMeasurements(row.state);
              // The label is the fallback, never the lead: a step no source named
              // still has to be identifiable.
              const title = row.question || row.label;
              const marker =
                row.kind === 'root'
                  ? t(PHASE_KEYS[ROOT_PHASE])
                  : row.keyQuestionPosition === null
                  ? ''
                  : row.kind === 'followUp'
                  ? `${row.keyQuestionPosition}.${row.subquestionN}`
                  : t('advisory:keyQuestions.marker', { position: row.keyQuestionPosition });
              const duration =
                measured && row.wallSeconds !== null ? durationKeyAndValue(row.wallSeconds) : null;
              // `running` and `done` become a mark; the other four keep their word.
              const glyph = row.state === 'running' || row.state === 'done';
              // A row with nothing behind it gets no control: the framing step reads
              // no earlier answers by construction, and an empty panel that opens to
              // nothing is worse than a row that does not offer to open.
              const hasDetail =
                row.steps.length > 0 || row.sources.length > 0 || row.blankSlots > 0;
              // Counts only once the row has measured something: mid-flight they are 0
              // because nothing has been measured, not because nothing was retrieved.
              const references =
                measured && row.edges > 0
                  ? t('advisory:evidence.runEvidence', { edges: row.edges })
                  : '';
              const metaText = [references, duration ? t(duration.key, duration.values) : '']
                .filter(Boolean)
                .join(' · ');
              return (
                <Box
                  component="li"
                  key={row.label}
                  data-testid="advisory-run-turn"
                  data-status={row.state}
                  sx={theme => ({
                    paddingY: 0.75,
                    borderBottom: `1px solid ${theme.visualPrimitives.border}`,
                    textAlign: 'left',
                  })}
                >
                  {/* Three lines, top to bottom: the question with its state mark,
                      what the step measured, then what kind of step it is with the
                      control that opens it. The mark sits on the title's line because
                      that is the line a reader scans; the chevron sits on the last one
                      because it belongs to what it opens. */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography
                      onClick={hasDetail ? () => toggle(row.label) : undefined}
                      sx={theme => ({
                        fontSize: theme.visualPrimitives.dzSubSize,
                        color: theme.colors.inkPrimary,
                        cursor: hasDetail ? 'pointer' : undefined,
                      })}
                    >
                      {marker ? (
                        <Typography
                          component="span"
                          sx={[
                            mono,
                            theme => ({
                              fontSize: theme.visualPrimitives.kqSubIndexSize,
                              color: theme.colors.lavenderDeep,
                              marginRight: theme.visualPrimitives.kqSubIndexGap,
                            }),
                          ]}
                        >
                          {marker}
                        </Typography>
                      ) : null}
                      {title}
                    </Typography>
                    {/* State and the row's share of the run's steps, in one pill. The
                        headline counts every turn ("7 of 7 steps"); folding the chains
                        away left nothing on screen that added up to it, and these are
                        what do: 1/1, 3/3, 3/3. */}
                    <Box
                      data-testid="advisory-run-turn-status"
                      sx={theme => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        flexShrink: 0,
                        padding: '2px 8px',
                        borderRadius: theme.visualPrimitives.radiusPill,
                        border: `1px solid ${theme.visualPrimitives.borderLight}`,
                      })}
                    >
                      {glyph ? (
                        <Box
                          sx={{
                            width: MARK_BOX,
                            height: MARK_BOX,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <StateGlyph state={row.state} label={t(STATE_KEYS[row.state])} />
                        </Box>
                      ) : (
                        <Typography
                          sx={theme => ({
                            fontSize: theme.visualPrimitives.kqSubIndexSize,
                            color: theme.colors.inkTertiary,
                            whiteSpace: 'nowrap',
                          })}
                        >
                          {t(STATE_KEYS[row.state])}
                        </Typography>
                      )}
                      <Box component="span" sx={SR_ONLY}>
                        {t('advisory:evidence.runProgress', {
                          done: row.stepsDone,
                          total: row.stepsTotal,
                        })}
                      </Box>
                      <Typography
                        aria-hidden
                        component="span"
                        data-testid="advisory-run-turn-steps"
                        sx={[
                          mono,
                          theme => ({
                            fontSize: theme.visualPrimitives.kqSubIndexSize,
                            color: theme.colors.inkSecondary,
                            whiteSpace: 'nowrap',
                          }),
                        ]}
                      >
                        {`${row.stepsDone}/${row.stepsTotal}`}
                      </Typography>
                    </Box>
                  </Box>

                  {metaText ? (
                    <Box
                      data-testid="advisory-run-turn-meta"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {/* Only beside a reference count, never beside a bare duration:
                          the glyph stands for the material the step read. */}
                      {references ? (
                        <DescriptionOutlinedIcon
                          aria-hidden
                          data-testid="advisory-run-turn-references-icon"
                          sx={theme => ({ fontSize: '15px', color: theme.colors.inkTertiary })}
                        />
                      ) : null}
                      <Typography
                        sx={theme => ({
                          fontSize: theme.visualPrimitives.dzSubSize,
                          color: theme.colors.inkTertiary,
                        })}
                      >
                        {metaText}
                      </Typography>
                    </Box>
                  ) : null}

                  {/* Dropped entirely when there is neither a phase to name nor
                      anything to open, rather than left as an empty line holding the
                      row open. */}
                  {phaseKey || hasDetail ? (
                    <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                      {phaseKey ? (
                        <Typography
                          sx={theme => ({
                            fontSize: theme.visualPrimitives.dzSubSize,
                            color: theme.colors.inkTertiary,
                          })}
                        >
                          {t(phaseKey)}
                        </Typography>
                      ) : null}
                      {hasDetail ? (
                        <IconButton
                          size="small"
                          data-testid="advisory-run-turn-expand"
                          aria-expanded={expanded}
                          aria-label={t(
                            expanded
                              ? 'advisory:evidence.collapseStep'
                              : 'advisory:evidence.expandStep'
                          )}
                          onClick={() => toggle(row.label)}
                          sx={[
                            focusRing,
                            theme => ({
                              color: theme.colors.inkTertiary,
                              width: CHEVRON_BOX,
                              height: CHEVRON_BOX,
                              padding: 0,
                              transition: 'transform 200ms ease',
                              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                            }),
                          ]}
                        >
                          <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </Box>
                  ) : null}

                  {/* `unmountOnExit`: a closed row has nothing in the document at all,
                      so a run does not carry a hidden panel per question inside a live
                      region. */}
                  <Collapse in={expanded} timeout={reduceMotion ? 0 : 200} unmountOnExit>
                    {/* `aria-live="off"` inside the polite region above, deliberately.
                        The reader opens this panel themselves and the button's
                        `aria-expanded` reports that; without the opt-out its contents
                        would be re-announced every time any other turn transitioned. */}
                    <Box
                      aria-live="off"
                      data-testid="advisory-run-turn-detail"
                      sx={{ paddingTop: 1, paddingBottom: 0.5, display: 'grid', gap: 0.75 }}
                    >
                      {/* Every step behind the row, the question's own first. Their
                          counts are what the row's count is a sum of, and each carries
                          the same marker the list and the question editor use — so the
                          arithmetic is checkable and nothing needs a sentence. */}
                      {row.steps.map(step => (
                        <SourceCard
                          key={step.label}
                          testId="advisory-run-turn-step"
                          marker={
                            row.keyQuestionPosition === null
                              ? ''
                              : step.subquestionN === null
                              ? t('advisory:keyQuestions.marker', {
                                  position: row.keyQuestionPosition,
                                })
                              : `${row.keyQuestionPosition}.${step.subquestionN}`
                          }
                          icon={<SubdirectoryArrowRightIcon fontSize="small" />}
                          title={step.question || step.label}
                          detail={
                            step.state === 'done' || step.state === 'partial'
                              ? step.edges > 0
                                ? t('advisory:evidence.runEvidence', { edges: step.edges })
                                : ''
                              : t(STATE_KEYS[step.state])
                          }
                        />
                      ))}

                      {/* What it read from OUTSIDE its own branch — one key question
                          that retrieved from another. The framing step is filtered out
                          upstream: it is an ancestor of every turn, so naming it here
                          would say the same thing under every question. */}
                      {row.sources.map(source => (
                        <SourceCard
                          key={source.label}
                          testId="advisory-run-turn-source"
                          icon={<FormatQuoteIcon fontSize="small" />}
                          title={source.question || source.label}
                          detail={PHASE_KEYS[source.phase] ? t(PHASE_KEYS[source.phase]) : ''}
                        />
                      ))}

                      {/* Per row, and deliberately the run-level warning's own copy:
                          it is the same statement at a smaller scope — a reference that
                          retrieval decided on and delivery did not deliver. */}
                      {row.blankSlots > 0 ? (
                        <Typography sx={[meta]} data-testid="advisory-run-turn-blank">
                          {t('advisory:evidence.runBlankWarning', { count: row.blankSlots })}
                        </Typography>
                      ) : null}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        ) : null}

        {/* The one number worth surfacing on its own. `totalEdges` says what retrieval
            DECIDED; blank slots say what the model actually RECEIVED, and for 184 V3
            executions those disagreed completely while every score looked healthy.
            `null` means the source never measured it — distinct from a measured 0. */}
        {view.totalBlankSlots !== null && view.totalBlankSlots > 0 ? (
          <Typography sx={[meta]} data-testid="advisory-run-blank-warning">
            {t('advisory:evidence.runBlankWarning', { count: view.totalBlankSlots })}
          </Typography>
        ) : null}

        {inFlight ? (
          // Not a promise this component invents: the run genuinely survives, because
          // every V3 turn is a detached Cloud Run Job and the project document records it.
          <Typography sx={[meta]}>{t('advisory:thinking.leaveAndReturn')}</Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export default AdvisoryRunProgress;
